import { create } from 'zustand';

/**
 * Request Context Store
 * 
 * Stores the latest request ID from API responses for traceability.
 * This allows the UI to display the request ID to users when errors occur,
 * enabling support teams to trace issues in the audit logs.
 */
interface RequestState {
  // The latest request ID from the most recent API response
  lastRequestId: string | null;
  // The timestamp of the last request
  lastRequestTimestamp: number | null;
  // Track if the last request resulted in an error
  lastRequestFailed: boolean;
  // The status code of the last failed request
  lastErrorStatus: number | null;
  // The URL that failed
  lastErrorUrl: string | null;
  
  // Actions
  setRequestId: (requestId: string) => void;
  setRequestError: (requestId: string, status: number, url: string) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  lastRequestId: null as string | null,
  lastRequestTimestamp: null as number | null,
  lastRequestFailed: false,
  lastErrorStatus: null as number | null,
  lastErrorUrl: null as string | null,
};

export const useRequestStore = create<RequestState>((set) => ({
  ...initialState,

  setRequestId: (requestId: string) =>
    set({
      lastRequestId: requestId,
      lastRequestTimestamp: Date.now(),
      lastRequestFailed: false,
      lastErrorStatus: null,
      lastErrorUrl: null,
    }),

  setRequestError: (requestId: string, status: number, url: string) =>
    set({
      lastRequestId: requestId,
      lastRequestTimestamp: Date.now(),
      lastRequestFailed: true,
      lastErrorStatus: status,
      lastErrorUrl: url,
    }),

  clearError: () =>
    set({
      lastRequestFailed: false,
      lastErrorStatus: null,
      lastErrorUrl: null,
    }),

  reset: () => set(initialState),
}));

/**
 * Selector hooks for common use cases
 */
export const useLastRequestId = () => useRequestStore((state) => state.lastRequestId);
export const useLastRequestFailed = () => useRequestStore((state) => state.lastRequestFailed);
export const useLastErrorStatus = () => useRequestStore((state) => state.lastErrorStatus);

/**
 * Get request store state outside of React components
 * Useful for API interceptors
 */
export const getRequestState = () => useRequestStore.getState();

/**
 * Check if the last error was a server error (5xx)
 */
export const useIsServerError = () =>
  useRequestStore((state) => {
    const status = state.lastErrorStatus;
    return status !== null && status >= 500 && status < 600;
  });

/**
 * Get a formatted error reference for user display
 */
export const useErrorReference = () =>
  useRequestStore((state) => {
    if (!state.lastRequestFailed || !state.lastRequestId) {
      return null;
    }
    return {
      requestId: state.lastRequestId,
      status: state.lastErrorStatus,
      timestamp: state.lastRequestTimestamp
        ? new Date(state.lastRequestTimestamp).toISOString()
        : null,
    };
  });
